import { Activity, AlertTriangle, CheckCircle2, Clock, Layers, Users, Wallet } from "lucide-react";
import DataTable from "./DataTable";
import StatCard from "./StatCard";
import StatCardGrid from "./StatCardGrid";
import CollectionFunnelChart from "./charts/CollectionFunnelChart";
import { motion } from "framer-motion";

export function formatSlaMinutes(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const mins = Number(value);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const rem = Math.round(mins % 60);
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
}

export function formatSlaDays(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const days = Number(value);
  if (days < 1) return `${Math.round(days * 24)}h`;
  return `${days.toFixed(days >= 10 ? 0 : 1)}d`;
}

function pct(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(0)}%`;
}

function StageTable({ title, empty, columns, rows }) {
  return (
    <div className="overflow-hidden rounded-xl border border-primary/[0.07] bg-white">
      <div className="border-b border-primary/[0.06] px-4 py-3">
        <h3 className="text-sm font-bold text-primary">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-primary/45">{empty}</p>
      ) : (
        <DataTable
          embedded
          paginate={false}
          compact
          data={rows}
          getRowId={(row, index) => row.key || row.id || index}
          columns={columns.map((col) => ({
            key: col.key,
            header: col.label,
            render: (row) => (col.render ? col.render(row) : row[col.key] ?? "—"),
          }))}
        />
      )}
    </div>
  );
}

function MixBars({ title, subtitle, rows, colors }) {
  const total = Math.max(1, rows.reduce((sum, row) => sum + (Number(row.count) || 0), 0));
  return (
    <div className="rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)]">
      <h3 className="text-sm font-bold text-primary">{title}</h3>
      {subtitle ? <p className="mt-0.5 text-xs text-primary/40">{subtitle}</p> : null}
      <div className="mt-4 flex h-4 overflow-hidden rounded-full bg-primary/[0.05]">
        {rows.map((row, i) => {
          const count = Number(row.count) || 0;
          const width = Math.max(count > 0 ? 4 : 0, Math.round((count / total) * 100));
          if (!width) return null;
          return (
            <motion.div
              key={row.label}
              className="h-full"
              style={{ backgroundColor: colors[i % colors.length], width: `${width}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${width}%` }}
              transition={{ duration: 0.6, delay: 0.08 * i }}
              title={`${row.label}: ${count}`}
            />
          );
        })}
      </div>
      <ul className="mt-4 space-y-2">
        {rows.map((row, i) => (
          <li key={row.label} className="flex items-center justify-between gap-2 text-xs">
            <span className="flex min-w-0 items-center gap-2 text-primary/70">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: colors[i % colors.length] }}
              />
              <span className="truncate capitalize">{row.label}</span>
            </span>
            <span className="font-bold tabular-nums text-primary">
              {(Number(row.count) || 0).toLocaleString()}
              <span className="ml-1 font-medium text-primary/35">
                ({Math.round(((Number(row.count) || 0) / total) * 100)}%)
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Shared SLA performance block for Activity pages (ops + provider).
 * @param {{ sla: object|null, loading?: boolean, showAgents?: boolean }} props
 */
export default function SlaPerformancePanel({ sla, loading = false, showAgents = true }) {
  const summary = sla?.summary || {};
  const targets = sla?.targets || {};
  const agents = Array.isArray(sla?.by_agent) ? sla.by_agent : [];
  const groups = Array.isArray(sla?.by_group) ? sla.by_group : [];
  const funnel = Array.isArray(sla?.funnel) ? sla.funnel : [];
  const breachMix = Array.isArray(sla?.breach_mix) ? sla.breach_mix : [];
  const stageMix = Array.isArray(sla?.stage_mix) ? sla.stage_mix : [];
  const assignDays = summary.assign_within_days ?? targets.assign_within_days ?? 5;
  const contactDays = summary.first_contact_within_days ?? targets.first_contact_within_days ?? 2;

  return (
    <div className="mb-5 space-y-4">
      <p className="text-xs text-primary/50">
        Digestion targets: assign within <span className="font-semibold text-primary">{assignDays} days</span> of
        upload · first contact within{" "}
        <span className="font-semibold text-primary">{contactDays} days</span> of assignment.
      </p>

      <StatCardGrid cols={5} className="mb-0">
        <StatCard
          label="Cases in SLA"
          value={loading ? "…" : String(summary.total_cases ?? 0)}
          sub={`${summary.assigned ?? 0} distributed`}
          icon={Clock}
          tone="primary"
        />
        <StatCard
          label="Assign on time"
          value={loading ? "…" : pct(summary.assign_on_time_pct)}
          sub={`${summary.assign_breached ?? 0} breached · avg ${formatSlaDays(summary.avg_upload_to_assign_days)}`}
          icon={Users}
          tone="accent"
        />
        <StatCard
          label="Contact on time"
          value={loading ? "…" : pct(summary.contact_on_time_pct)}
          sub={`${summary.contact_breached ?? 0} breached · avg ${formatSlaDays(summary.avg_assign_to_contact_days)}`}
          icon={Activity}
          tone="collect"
        />
        <StatCard
          label="Payments started"
          value={loading ? "…" : String(summary.payment_initiated ?? 0)}
          sub={`Avg upload→start ${formatSlaMinutes(summary.avg_upload_to_payment_minutes)}`}
          icon={Wallet}
          tone="amber"
        />
        <StatCard
          label="Payments completed"
          value={loading ? "…" : String(summary.payment_completed ?? 0)}
          sub={`Avg upload→done ${formatSlaMinutes(summary.avg_upload_to_payment_completed_minutes)}`}
          icon={CheckCircle2}
          tone="emerald"
        />
      </StatCardGrid>

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="rounded-xl border border-primary/[0.07] bg-white p-4 lg:col-span-7">
          <h3 className="text-sm font-bold text-primary">Digestion funnel</h3>
          <p className="mt-0.5 mb-4 text-xs text-primary/40">
            Upload → assign → first contact → payment start → fully paid
          </p>
          {loading && !funnel.length ? (
            <p className="py-8 text-center text-sm text-primary/45">Loading funnel…</p>
          ) : (
            <CollectionFunnelChart
              stages={
                funnel.length
                  ? funnel.map((row) => ({ label: row.label, count: row.count }))
                  : [
                      { label: "Uploaded", count: summary.total_cases ?? 0 },
                      { label: "Assigned", count: summary.assigned ?? 0 },
                      { label: "First contact", count: summary.contacted ?? 0 },
                      { label: "Payment started", count: summary.payment_initiated ?? 0 },
                      { label: "Fully paid", count: summary.payment_completed ?? 0 },
                    ]
              }
            />
          )}
        </div>
        <div className="grid gap-4 lg:col-span-5">
          <MixBars
            title="Breach mix"
            subtitle="Where digestion is slipping"
            rows={
              breachMix.length
                ? breachMix
                : [
                    {
                      label: "On track",
                      count: Math.max(
                        0,
                        (summary.total_cases ?? 0) -
                          (summary.assign_breached ?? 0) -
                          (summary.contact_breached ?? 0)
                      ),
                    },
                    { label: "Assign breach", count: summary.assign_breached ?? 0 },
                    { label: "Contact breach", count: summary.contact_breached ?? 0 },
                  ]
            }
            colors={["#347a62", "#c9a209", "#B91C1C"]}
          />
          <MixBars
            title="Current stage mix"
            subtitle="Where cases sit right now"
            rows={stageMix}
            colors={["#64748b", "#0369a1", "#7c3aed", "#c9a209", "#347a62"]}
          />
        </div>
      </div>

      <div className={`grid gap-4 ${showAgents ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {showAgents ? (
          <StageTable
            title="Agent KPI — contact & payments"
            empty={loading ? "Loading agent SLA…" : "No agent SLA data yet."}
            rows={agents.map((row) => ({ ...row, key: row.agent_slug || row.agent_name }))}
            columns={[
              { key: "agent_name", label: "Agent" },
              { key: "cases", label: "Cases" },
              { key: "contacted", label: "Contacted" },
              {
                key: "contact_on_time_pct",
                label: "On-time %",
                render: (row) => pct(row.contact_on_time_pct),
              },
              {
                key: "contact_breached",
                label: "Breached",
                render: (row) => (
                  <span className="inline-flex items-center gap-1">
                    {(row.contact_breached ?? 0) > 0 ? (
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                    ) : null}
                    {row.contact_breached ?? 0}
                  </span>
                ),
              },
              {
                key: "payment_initiated",
                label: "Pay start",
                render: (row) => row.payment_initiated ?? 0,
              },
              {
                key: "payment_completed",
                label: "Pay done",
                render: (row) => row.payment_completed ?? 0,
              },
              {
                key: "avg_assign_to_contact_minutes",
                label: "Assign→contact",
                render: (row) => formatSlaMinutes(row.avg_assign_to_contact_minutes),
              },
            ]}
          />
        ) : null}

        <StageTable
          title="Batch KPI — assign digestion"
          empty={loading ? "Loading batch SLA…" : "No batch SLA data yet."}
          rows={groups.map((row) => ({
            ...row,
            key: row.portfolio_group_slug || row.group_name,
          }))}
          columns={[
            {
              key: "group_name",
              label: "Batch",
              render: (row) => (
                <span className="inline-flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-primary/35" />
                  {row.group_name || row.portfolio_group_slug || "—"}
                </span>
              ),
            },
            { key: "cases", label: "Cases" },
            { key: "assigned", label: "Assigned" },
            {
              key: "assign_on_time_pct",
              label: "On-time %",
              render: (row) => pct(row.assign_on_time_pct),
            },
            {
              key: "assign_breached",
              label: "Breached",
              render: (row) => row.assign_breached ?? 0,
            },
            {
              key: "payment_completed",
              label: "Paid",
              render: (row) => row.payment_completed ?? 0,
            },
            {
              key: "avg_upload_to_assign_days",
              label: "Upload→assign",
              render: (row) =>
                row.avg_upload_to_assign_days != null
                  ? formatSlaDays(row.avg_upload_to_assign_days)
                  : formatSlaMinutes(row.avg_upload_to_assign_minutes),
            },
          ]}
        />
      </div>
    </div>
  );
}
